/**
 * @fileoverview Utility functions for JsonForms renderers in the Storyteller application.
 * Contains helper functions for rendering child elements and common interfaces.
 *
 * @example
 * // Basic usage of renderChildren
 * const children = renderChildren(
 *   layout,
 *   schema,
 *   'container-class',
 *   'form/path',
 *   true
 * )
 *
 * @example
 * // Using RenderChildrenProps interface
 * function MyRenderer({ layout, schema, className, path }: RenderChildrenProps) {
 *   return renderChildren(layout, schema, className, path, true)
 * }
 */

import { JsonSchema, Layout } from '@jsonforms/core'
import { JsonFormsDispatch, useJsonForms } from '@jsonforms/react'

/**
 * @interface RenderChildrenProps
 * @description Interface for the props required by the renderChildren function.
 * @property {Layout} layout - The layout object containing the child elements.
 * @property {JsonSchema} schema - The schema object for the form.
 * @property {string} className - The class name for the container element.
 * @property {string} path - The path to the current form element.
 */
export interface RenderChildrenProps {
    layout: Layout
    schema: JsonSchema
    className: string
    path: string
}

/**
 * @function renderChildren
 * @description Renders child elements based on the provided layout, schema, and path.
 * @param {Layout} layout - The layout object containing the child elements.
 * @param {JsonSchema} schema - The schema object for the form.
 * @param {string} className - The class name for the container element.
 * @param {string} path - The path to the current form element.
 * @param {boolean} enabled - Whether the form is enabled.
 * @returns {JSX.Element[]} An array of JSX elements representing the child elements.
 */
export const renderChildren = (
    layout: Layout,
    schema: JsonSchema,
    className: string,
    path: string,
    enabled: boolean
) => {
    const { renderers, cells } = useJsonForms()

    return layout.elements.map((child, index) => {
        return (
            <div className={className} key={`${path}-${index}`}>
                <JsonFormsDispatch
                    renderers={renderers}
                    cells={cells}
                    uischema={child}
                    schema={schema}
                    path={path}
                    enabled={enabled}
                />
            </div>
        )
    })
}
